import { useMutation } from '@tanstack/react-query';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';
import {
    cancelPaymentPlan,
    confirmUnfulfillOrder,
    copyUpdateBillingDetailsLink,
    downloadReceipt,
    fetchRevenueChart,
    fulfillOrder,
    getPaymentDetails,
    refundOrder,
    resendBooking,
    resendDigitalDownload,
    resendOrder,
    sendPaymentFailedReminder,
    sendReceipt,
    submitDispute,
    uploadDisputeEvidenceFile,
} from '@/lib/api/income/api';
import { User } from '@sentry/react';
import { useStanQuery } from '.';

// Payment Details Query
export function usePaymentDetails(paymentId: number, enabled: boolean = true) {
    return useStanQuery({
        queryKey: [QUERY_KEYS.PAYMENT_DETAILS, paymentId],
        queryFn: () => getPaymentDetails(paymentId),
        enabled: enabled && !!paymentId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        meta: { error: { sentryMessage: '[PAYMENT_DETAILS] failed-to-fetch-payment-details' } },
    });
}

// Digital Download Mutation
export function useResendDigitalDownload() {
    return useMutation({
        mutationKey: [QUERY_KEYS.DIGITAL_DOWNLOAD],
        mutationFn: (paymentId: string) => resendDigitalDownload(paymentId),
    });
}

// Booking Mutation
export function useResendBooking() {
    return useMutation({
        mutationKey: [QUERY_KEYS.BOOKING],
        mutationFn: (paymentId: string) => resendBooking(paymentId),
    });
}

// Send Receipt Mutation
export function useSendReceipt() {
    return useMutation({
        mutationKey: [QUERY_KEYS.SEND_RECEIPT],
        mutationFn: ({ user, paymentId }: { user: User; paymentId: string }) => sendReceipt(user, paymentId),
    });
}

// Download Receipt Mutation
export function useDownloadReceipt() {
    return useMutation({
        mutationKey: [QUERY_KEYS.DOWNLOAD_RECEIPT],
        mutationFn: ({ user, paymentId }: { user: User; paymentId: string }) => downloadReceipt(user, paymentId),
    });
}

// Copy Billing Link Mutation
export function useCopyBillingLink() {
    return useMutation({
        mutationKey: [QUERY_KEYS.BILLING_LINK],
        mutationFn: (paymentPlanId: string) => copyUpdateBillingDetailsLink(paymentPlanId),
    });
}

// Cancel Payment Plan Mutation
export function useCancelPaymentPlan() {
    return useMutation({
        mutationKey: [QUERY_KEYS.PAYMENT_PLAN],
        mutationFn: (paymentPlanId: string) => cancelPaymentPlan(paymentPlanId),
    });
}

// Refund Order Mutation
export function useRefundOrder() {
    return useMutation({
        mutationKey: [QUERY_KEYS.REFUND_ORDER],
        mutationFn: ({
            orderId,
            token,
            isAdminLoggedInAsUser,
            user_id,
        }: {
            orderId: string;
            token: string | null;
            isAdminLoggedInAsUser?: boolean;
            user_id?: number | string;
        }) => refundOrder(orderId, token, isAdminLoggedInAsUser, user_id),
    });
}

// Fulfill Order Mutation
export function useFulfillOrder() {
    return useMutation({
        mutationKey: [QUERY_KEYS.FULFILL_ORDER],
        mutationFn: ({ orderId, fileData }: { orderId: string | number; fileData?: Record<string, any> }) =>
            fulfillOrder(orderId, fileData),
    });
}

// Confirm Unfulfill Order Mutation
export function useConfirmUnfulfillOrder() {
    return useMutation({
        mutationKey: [QUERY_KEYS.UNFULFILL_ORDER],
        mutationFn: (paymentId: string | number) => confirmUnfulfillOrder(paymentId),
    });
}

// Resend Order Mutation
export function useResendOrder() {
    return useMutation({
        mutationKey: [QUERY_KEYS.RESEND_ORDER],
        mutationFn: ({ emailToSendTo, orderId }: { emailToSendTo: string; orderId: string | number }) =>
            resendOrder(emailToSendTo, orderId),
    });
}

// Send Payment Failed Reminder Mutation
export function useSendPaymentFailedReminder() {
    return useMutation({
        mutationKey: [QUERY_KEYS.PAYMENT_DETAILS],
        mutationFn: (paymentId: string | number) => sendPaymentFailedReminder(paymentId),
    });
}

// Upload Dispute Evidence Mutation
export function useUploadDisputeEvidence() {
    return useMutation({
        mutationKey: [QUERY_KEYS.DISPUTE_EVIDENCE],
        mutationFn: (file: File) => uploadDisputeEvidenceFile(file),
    });
}

// Submit Dispute Mutation
export function useSubmitDispute() {
    return useMutation({
        mutationKey: [QUERY_KEYS.SUBMIT_DISPUTE],
        mutationFn: (payload: {
            payment_id: string;
            customer_communication_file_id?: number;
            uncategorized_text?: string;
        }) => submitDispute(payload),
    });
}

export function useFetchRevenueChart() {
    return useStanQuery({
        queryKey: [QUERY_KEYS.REVENUE_CHART],
        queryFn: fetchRevenueChart,
        staleTime: 5 * 60 * 1000, // 5 minutes
        placeholderData: {
            series: [],
            options: {
                yaxis: {
                    min: 0,
                },
                xaxis: {
                    categories: [],
                },
            },
        },
        meta: { error: { sentryMessage: '[INCOME] failed-to-fetch-revenue-chart', feature: SENTRY_FEATURES.INCOME } },
    });
}
